import * as React from 'react';
import {
  Keyboard,
  Platform,
  ScrollView,
  StyleSheet,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import {FlashList} from '@shopify/flash-list';
import {withForwardedNavigationParams} from 'react-navigation-props-mapper';
import BackSubheader from '../../elements/BackSubheader';
import CheckoutBar from '../../elements/orderAhead/CheckoutBar';
import type {LocationType} from 'src/types/Location';
import {
  CategoryType,
  IMenuCategory,
  ProductCategory,
} from 'src/types/ProductCategory';
import MenuService from 'src/services/MenuService';
import Styles from '../../Styles';
import KeyboardAvoidingView from '../../elements/365KeyboardAvoidingView';
import NavActions from 'src/actions/NavActions';
import AppRoutes from 'src/AppRoutes';
import AccountConstants from 'src/constants/AccountConstants';
import TransactionStore from 'src/stores/TransactionStore';
import ProductCategories from '../../elements/cart/ProductCategories';
import {
  type MenuCategoryType,
  type MenuProductType,
  PickUpTimeType,
} from 'src/types/Menu';
import CartTypes from 'src/constants/cart/CartTypes';
import Localized from 'src/constants/AppStrings';
import {alertError} from '../../helpers/AlertHelper';
import {DNA} from 'src/types/dnaTypes';
import MenuActions from 'src/actions/MenuActions';
import Events from 'src/logging/Events';
import {generateErrorMessage} from 'src/logging/generateErrorMessage';
import uuid from 'src/nativeModules/UUID';
import MainConsumerContext from 'src/components/MainConsumerContext';
import {NavigationProp, RouteProp} from '@react-navigation/native';
import {getPreviousRouteName} from 'src/Util';
import FirebaseAnalytic from '../../../nativeModules/FirebaseAnalytic';
import moment from 'moment';
import TransactionActions from 'src/actions/TransactionActions';
import Settings from 'src/Settings';
import CrashlyticsEvents from 'src/logging/Crashlytics';
import {getDescriber} from 'src/components/screens/orderAhead/descriptor/DescriptorType';
import ProductSearchContainer from 'src/components/elements/orderAhead/ProductSearchComponent';
import NewSubMenu from 'src/components/elements/orderAhead/NewSubMenu';
import AppLifecycleTracker from 'src/components/utils/AppLifecycleTracker';
import {QuickAddOns} from '../../elements/cart/QuickAddOns';
import AccountStore from 'src/stores/AccountStore';
import PromotionTypes from 'src/constants/cart/PromotionTypes';
import CartService from 'src/services/CartService';
import ErrorModal from 'src/components/elements/ErrorModal';
import AVText from 'src/components/elements/AVText';
import RecentComboIcon from 'src/components/img/svg/RecentComboIcon';
import MenuItemListTemplate2 from 'src/components/elements/orderAhead/MenuListItemTemplate2';
import {scanAndPayTemplate} from 'src/components/screens/scanAndPay/templates/scanandPayThemeHelper';
import {CartItemType} from 'src/components/elements/cart/CartToastView';
import {
  withGlobalize,
  WithGlobalizeProps,
} from '../../../i18n/HOC/withGlobalize';
import EmptyOrderIcon from 'src/components/img/svg/EmptyOrdersIcon';

const {menuListStyles} = getDescriber();

type LineCounts = {
  [index: number]: number;
};

type MenuScreenProps = WithGlobalizeProps & {
  location: LocationType;
  navigation: NavigationProp<MenuScreen>;
  route: RouteProp<any>;
};
type MenuScreenState = {
  items: Array<MenuProductType | MenuCategoryType>;
  categories: Array<ProductCategory>;
  menuItems: Array<IMenuCategory>;
  selectedCategory: ProductCategory;
  searchValue: string;
  dnaConfiguration: DNA;
  previousRoute: string | null;
  lineCounts: LineCounts;
  extraPadding: {[index: number]: number};
  refreshKey: number;
  rewardItemAvailable?: boolean;
};

class MenuScreen extends React.Component<MenuScreenProps, MenuScreenState> {
  pickupTimeout: any;
  static contextType = MainConsumerContext;
  declare context: React.ContextType<typeof MainConsumerContext>;
  constructor(props: MenuScreenProps) {
    super(props);
    this.getMenuCategories = this.getMenuCategories.bind(this);
    this.getProducts = this.getProducts.bind(this);
    this.renderItem = this.renderItem.bind(this);
    this.onSearchChanged = this.onSearchChanged.bind(this);
    this.goToProductDetail = this.goToProductDetail.bind(this);
    this.goToCart = this.goToCart.bind(this);
    this.onCategoryPress = this.onCategoryPress.bind(this);
    this.onMenuItemPress = this.onMenuItemPress.bind(this);
    this.productAdded = this.productAdded.bind(this);
    this.onTransactionStoreChanged = this.onTransactionStoreChanged.bind(this);
    this.goToRecentOrders = this.goToRecentOrders.bind(this);
    this.checkMenuHasProducts = this.checkMenuHasProducts.bind(this);
    this.checkRewardProductMenuItem =
      this.checkRewardProductMenuItem.bind(this);
    this.renderRewardProductNotAvailableError =
      this.renderRewardProductNotAvailableError.bind(this);
    this.continuewithOrderAction = this.continuewithOrderAction.bind(this);
    this.onModalCancelPress = this.onModalCancelPress.bind(this);
    this.getDiscountCode = this.getDiscountCode.bind(this);

    const categories = this.getMenuCategories()?.categories;
    const menuItems = this.getMenuCategories()?.menuItems;
    const selectedCategory = categories.length > 0 ? categories[0] : null;

    const items =
      selectedCategory?.type === CategoryType.Product
        ? []
        : selectedCategory?.type === CategoryType.TopMenu
        ? this.getSubMenus(selectedCategory?.key)
        : this.getProducts(selectedCategory?.key);

    this.state = {
      items,
      categories,
      menuItems,
      selectedCategory,
      searchValue: '',
      dnaConfiguration: null,
      previousRoute: null,
      lineCounts: {},
      extraPadding: {},
      refreshKey: 0,
      rewardItemAvailable: true,
    };
    this.pickupTimeout = TransactionStore.getPickupTime();
    TransactionStore.addChangeListener(this.onTransactionStoreChanged);
  }

  componentDidMount(): void {
    FirebaseAnalytic.trackEvent('componentDidMount', 'MenuScreen', {
      ...this.props,
      ...this.state,
    });

    const previousRoute = getPreviousRouteName(
      this.props.navigation?.getState()?.routes,
    );
    this.setState({previousRoute});
    this.checkRewardProductMenuItem();
  }

  componentWillUnmount() {
    TransactionStore.removeChangeListener(this.onTransactionStoreChanged);
  }
  onTransactionStoreChanged() {
    const categories = this.getMenuCategories()?.categories;
    const selectedCategory: ProductCategory = categories?.[0];
    const items =
      selectedCategory?.type === CategoryType.Product
        ? []
        : selectedCategory?.type === CategoryType.TopMenu
        ? this.getSubMenus(selectedCategory?.key)
        : this.getProducts(selectedCategory?.key);

    this.setState(
      {
        categories,
        items,
      },
      () => {
        FirebaseAnalytic.trackEvent('onTransactionStoreChanged', 'MenuScreen', {
          ...this.props,
          ...this.state,
        });
      },
    );
    const cartItems = TransactionStore.getDisplayItems();
    if (cartItems.length > 0) {
      AppLifecycleTracker.updateCartState(true);
    }
  }

  componentDidUpdate(prevProps: MenuScreenProps) {
    const oldKey = prevProps.route?.params?.key;
    const newKey = this.props.route?.params?.key;

    if (newKey && newKey !== oldKey) {
      const {categories, menuItems} = this.getMenuCategories();
      this.setState({categories, menuItems});
    }
  }

  async goToCart() {
    const pickupTime = TransactionStore.getPickupTime();
    FirebaseAnalytic.trackEvent('goToCart', 'MenuScreen', {
      ...this.props,
      ...this.state,
      marketName: this.props.location.name,
      locationId: this.props.location.locationId,
      locationType: AccountConstants.SOS_LOCATION_TYPE,
      beaconId: this.props.location.beaconId ?? this.props.location.locationId,
      cartType: CartTypes.OrderAhead,
      location: this.props.location,
      showPreparationMethod:
        this.props.location.onlineOrderConfig.hasDiningPreference,
      defaultPreparationMethod:
        this.props.location.onlineOrderConfig.diningPreference,
      navigate: AppRoutes.Scan,
    });
    const timeZoneOffsetMinutes = MenuService.getTimezoneOffsetMinutes();
    const leadTime = this.props.location.onlineOrderConfig.kitchenSchedule;
    const now = moment()
      .add(leadTime, 'minutes')
      .add(timeZoneOffsetMinutes, 'minutes');
    if (
      !moment(pickupTime.date).isAfter(now) &&
      !(pickupTime.time === PickUpTimeType.ASAP)
    ) {
      try {
        this.context.actions.showSpinner();
        const availableTimes = await MenuActions.getAvailableTimeSlots(
          this.props.location,
          TransactionStore.getPickupLocationId(),
        );
        alertError(
          Localized.Errors.pickup_time_not_available,
          undefined,
          () => {
            TransactionActions.shoppingCartTransactionsCleared();
            if (Settings.isNewUI()) {
              NavActions.replace(AppRoutes.NewPickupTime, {
                availableTimes: MenuService.getAvailableTimeSlotsFromList(
                  availableTimes,
                  this.props.location.onlineOrderConfig.kitchenSchedule,
                ),
                location: this.props.location,
                selectedPickupLocation: TransactionStore.getPickupLocation(),
              });
            } else {
              NavActions.replace(AppRoutes.PickupTime, {
                strings: Localized,
                location: this.props.location,
                availableTimes,
              });
            }
          },
        );
      } catch (error) {
        const guid = await uuid.getRandomUUID();
        CrashlyticsEvents.log(
          'Exception',
          'PickupTimeScreen:PickupTimeSelected',
          generateErrorMessage(error),
          guid,
        );
        Events.Error.trackEvent(
          'Exception',
          'PickupTimeScreen:PickupTimeSelected',
          generateErrorMessage(error),
          guid,
        );
      } finally {
        this.context.actions.hideSpinner();
      }
    } else {
      this.context.actions.showSpinner();
      const availableTimes = await MenuActions.getAvailableTimeSlots(
        this.props.location,
        TransactionStore.getPickupLocationId(),
      );
      if (availableTimes && availableTimes.length > 0) {
        const isTimeSlotPresent = availableTimes.some(
          (obj) =>
            (obj.timeString === pickupTime.timeString &&
              moment(pickupTime.date).isSame(moment(obj.date), 'day')) ||
            pickupTime.time === PickUpTimeType.ASAP,
        );
        if (
          (moment(pickupTime.date).isAfter(now) ||
            pickupTime.time === PickUpTimeType.ASAP) &&
          isTimeSlotPresent
        ) {
          this.context.actions.hideSpinner();
          NavActions.push(AppRoutes.Scan, {
            marketName: this.props.location.name,
            locationId: this.props.location.locationId,
            locationType: AccountConstants.SOS_LOCATION_TYPE,
            beaconId:
              this.props.location.beaconId ?? this.props.location.locationId,
            cartType: CartTypes.OrderAhead,
            location: this.props.location,
            showPreparationMethod:
              this.props.location.onlineOrderConfig.hasDiningPreference,
            defaultPreparationMethod:
              this.props.location.onlineOrderConfig.diningPreference,
          });
        } else {
          this.context.actions.hideSpinner();
          alertError(
            Localized.Errors.order_limit_reached_for_pickup_time,
            undefined,
            () => {
              TransactionActions.shoppingCartTransactionsCleared();
              if (Settings.isNewUI()) {
                NavActions.replace(AppRoutes.NewPickupTime, {
                  availableTimes: MenuService.getAvailableTimeSlotsFromList(
                    availableTimes,
                    this.props.location.onlineOrderConfig.kitchenSchedule,
                  ),
                  location: this.props.location,
                  selectedPickupLocation: TransactionStore.getPickupLocation(),
                });
              } else {
                NavActions.replace(AppRoutes.PickupTime, {
                  strings: Localized,
                  location: this.props.location,
                  availableTimes,
                });
              }
            },
          );
        }
      } else {
        this.context.actions.hideSpinner();
        NavActions.push(AppRoutes.Scan, {
          marketName: this.props.location.name,
          locationId: this.props.location.locationId,
          locationType: AccountConstants.SOS_LOCATION_TYPE,
          beaconId:
            this.props.location.beaconId ?? this.props.location.locationId,
          cartType: CartTypes.OrderAhead,
          location: this.props.location,
          showPreparationMethod:
            this.props.location.onlineOrderConfig.hasDiningPreference,
          defaultPreparationMethod:
            this.props.location.onlineOrderConfig.diningPreference,
        });
      }
    }
  }

  goToProductDetail = async (product: MenuProductType) => {
    try {
      this.context.actions.showSpinner();
      const response = await MenuActions.getModifiers(
        this.props.location.locationId,
        product,
      );

      FirebaseAnalytic.trackEvent('goToProductDetail', 'MenuScreen', {
        ...this.props,
        ...this.state,
        strings: Localized,
        location: this.props.location,
        product,
        response,
        navigate: AppRoutes.ProductDetail,
      });
      NavActions.push(AppRoutes.ProductDetail, {
        strings: Localized,
        alertError: alertError,
        location: this.props.location,
        product,
        productAdded: this.productAdded,
      });
    } catch (error) {
      const guid = await uuid.getRandomUUID();
      CrashlyticsEvents.log(
        'Exception',
        'MenuScreen:goToProductDetail',
        generateErrorMessage(error),
        guid,
      );
      Events.Error.trackEvent(
        'Exception',
        'MenuScreen:goToProductDetail',
        generateErrorMessage(error),
        guid,
      );
      alertError(Localized.Errors.internet_issue, guid);
    } finally {
      this.context.actions.hideSpinner();
    }
  };

  goToRecentOrders() {
    FirebaseAnalytic.trackEvent('goToRecentOrders', 'MenuScreen', {
      ...this.props,
      ...this.state,
      location: this.props.location,
      navigate: AppRoutes.RecentOrders,
    });
    NavActions.push(AppRoutes.RecentOrders, {
      location: this.props.location,
    });
  }

  onSearchChanged(text: string) {
    const {selectedCategory} = this.state;
    const items =
      selectedCategory?.type === CategoryType.Product
        ? []
        : selectedCategory?.type === CategoryType.TopMenu
        ? MenuService.searchSubMenus(text, selectedCategory.key)
        : MenuService.searchProducts(text, selectedCategory.key);
    this.setState(
      {
        items,
        searchValue: text,
      },
      () => {
        FirebaseAnalytic.trackEvent('onSearchChanged', 'MenuScreen', {
          ...this.props,
          ...this.state,
        });
      },
    );
  }

  checkIfTopMenu(item?: ProductCategory) {
    return item?.type === CategoryType.TopMenu
      ? MenuService.searchSubMenus(this.state.searchValue, item?.key)
      : MenuService.searchProducts(this.state.searchValue, item?.key);
  }
  onCategoryPress(category: ProductCategory) {
    FirebaseAnalytic.trackEvent('onCategoryPress', 'MenuScreen', {
      ...this.props,
      ...this.state,
      category,
    });

    const items =
      category?.type === CategoryType.Product
        ? []
        : this.checkIfTopMenu(category);

    this.setState({items}, () => {
      this.setState({
        selectedCategory: category,
      });
    });
  }

  getDiscountCode() {
    return this.props.location.locationFeatures.is365PayAfterTaxPromo &&
      TransactionStore.isAfterTaxPromoApplied()
      ? PromotionTypes.AfterTaxPromo
      : null;
  }

  async onMenuItemPress(category: IMenuCategory, isPlusIconPressed?: boolean) {
    const params = {...this.props, ...this.state};
    FirebaseAnalytic.trackEvent('onMenuItemPress', 'MenuScreen', {
      ...params,
      category,
    });

    const isAddToCartWithoutRedirections =
      !!isPlusIconPressed && !category.isHasModifiers;

    const product: MenuProductType | null = MenuService.getFirstProduct(
      category.key,
    );

    if (product && !isAddToCartWithoutRedirections) {
      this.goToProductDetail(product);
    } else {
      const prepDefaults = TransactionStore.getPreparationMethodDefaults(
        this.props.location,
      );

      const discountCode = this.getDiscountCode();

      const items = [...TransactionStore.getAllItems()];
      const newItem = {
        BarCode: product.scancode,
        PrintGroups: product.printgroups,
      };

      const newItems = [...items, newItem];
      this.context.actions.showSpinner();

      const result = await CartService.updateCart(
        this.props.location.locationId,
        AccountStore.getAccountId(),
        [],
        newItems,
        AccountConstants.SOS_LOCATION_TYPE,
        category.scanCode,
        TransactionStore.getPickupTime(),
        prepDefaults.selectedMethod,
        this.context,
        discountCode,
      );
      this.context.actions.showToast(CartItemType.Menu);
      FirebaseAnalytic.trackEvent('addItem', 'ProductDetailScreen', {
        ...params,
        prepDefaults,
        items,
        result,
      });
    }
  }

  productAdded() {
    // Clear the search
    this.onSearchChanged('');
  }

  checkMenuHasProducts(category: MenuCategoryType, type: string) {
    FirebaseAnalytic.trackEvent('checkMenuHasProducts', 'MenuScreen', {
      ...this.props,
      ...this.state,
      category,
      type,
    });

    switch (type) {
      case CategoryType.All:
        if (MenuService.searchProducts('', -1).length <= 0) {
          return false;
        }
        break;
      case CategoryType.Menu:
        if (category.products.length <= 0) {
          return false;
        }
        break;
      case CategoryType.TopMenu:
        if (MenuService.searchSubMenus('', category.id).length <= 0) {
          return false;
        }
        break;
    }
    return true;
  }

  getMenuCategories() {
    const categories = [];
    const menuItems = [];
    MenuService.getMenuCategories().map((category) => {
      const isProduct =
        category.isautoadd === 'Y' && category.products?.length === 1;

      const type = category.default
        ? CategoryType.All
        : isProduct
        ? CategoryType.Product
        : category.products.length > 0
        ? CategoryType.Menu
        : CategoryType.TopMenu;

      if (this.checkMenuHasProducts(category, type)) {
        if (type === CategoryType.Product) {
          menuItems.push({
            key: category.id,
            name: category.longname,
            iconName: 'bars',
            imageUrl:
              category.products?.[0].smallimageurl ||
              category.products?.[0].largeimageurl,
            type,
            isHasModifiers: category.products?.[0].hasmodifier === 'Y',
            rewardPoints: category.products?.[0].rewardPoints,
            scanCode: category.products?.[0].scancode,
          });
          return;
        }
        categories.push({
          key: category.default ? -1 : category.id,
          name: category.longname,
          iconName: 'bars',
          type,
        });
      }
    });

    FirebaseAnalytic.trackEvent('getMenuCategories', 'MenuScreen', {
      ...this.props,
      ...this.state,
      categories,
    });

    return {categories, menuItems};
  }

  getProducts(menuId: number | string = -1) {
    return MenuService.searchProducts('', menuId);
  }

  getSubMenus(menuId: number | string = -1) {
    return MenuService.searchSubMenus('', menuId);
  }

  continuewithOrderAction = () => {
    this.setState({rewardItemAvailable: true});
  };
  onModalCancelPress = () => {
    if (Platform.OS === 'web') {
      NavActions.navigate(AppRoutes.Home, {
        screen: AppRoutes.RewardsScreen,
      });
    } else {
      NavActions.navigate(AppRoutes.RewardsScreen);
    }
  };

  renderRewardProductNotAvailableError = () => {
    const {rewardItemAvailable} = this.state;
    if (rewardItemAvailable) {
      return null;
    }
    return (
      <ErrorModal
        isModalVisible={true}
        headerTitle={Localized.Labels.product_unavailable}
        description={Localized.Labels.that_product_is_currently_unavailable}
        button1Title={Localized.Buttons.continue_with_order}
        button2Title={Localized.Buttons.cancel}
        onModalClose={this.continuewithOrderAction}
        button1Action={this.continuewithOrderAction}
        button2Action={this.onModalCancelPress}
      />
    );
  };

  checkRewardProductMenuItem = async () => {
    const rewardProductData = TransactionStore.getrewardItem();
    if (!this.isValidRewardItem(rewardProductData)) {
      return;
    }
    const isItemAvailable = this.state.items?.some(
      (item) => item?.id === rewardProductData?.Id,
    );

    if (!isItemAvailable) {
      this.setState({
        rewardItemAvailable: false,
      });
      return;
    }
    const rewardMenuItem = MenuService.rewardProductsSearch(
      rewardProductData?.Id,
    );
    if (rewardMenuItem?.length === 0) {
      return;
    }
    const response = await this.updateRewardCart(rewardProductData);
    if (!response && this.state.rewardItemAvailable) {
      this.setState({rewardItemAvailable: false});
    } else if (response) {
      this.context.actions.showToast(CartItemType.Reward);
    }
  };
  isValidRewardItem = (rewardProductData) => {
    return rewardProductData?.BarCode || rewardProductData?.Id;
  };

  updateRewardCart = async (rewardProductData) => {
    const prepMethodDefaults = TransactionStore.getPreparationMethodDefaults(
      this.props.location,
    );
    const discountCode =
      this.props.location.locationFeatures.is365PayAfterTaxPromo &&
      TransactionStore.isAfterTaxPromoApplied()
        ? PromotionTypes.AfterTaxPromo
        : null;

    return await CartService.updateCart(
      this.props.location.locationId,
      AccountStore.getAccountId(),
      [],
      [{BarCode: rewardProductData?.BarCode, isFreeRewardProduct: true}],
      AccountConstants.SOS_LOCATION_TYPE,
      rewardProductData?.BarCode,
      TransactionStore.getPickupTime(),
      prepMethodDefaults.selectedMethod,
      this.context,
      discountCode,
      true,
    );
  };

  renderListEmpty = () => (
    <View
      style={[
        styles.listEmptyStyles,
        this.state.menuItems?.length && styles.marginStyle,
      ]}
    >
      <AVText style={styles.emptyText}>{Localized.Labels.no_pdt_found}</AVText>
      <View style={styles.emptyIconContainer}>
        {Settings.isAramark() ? (
          <EmptyOrderIcon />
        ) : (
          <RecentComboIcon
            fillColor={scanAndPayTemplate.emptyCartColors.primaryFill}
            secondaryColor={scanAndPayTemplate.emptyCartColors.secondaryFill}
          />
        )}
      </View>
    </View>
  );
  renderFooterComponent = () => <View style={styles.footer} />;

  renderItem({item, index}) {
    const imageURL = item?.smallimageurl || null;
    if (item?.parent) {
      return (
        <NewSubMenu
          menu={item}
          dnaConfiguration={this.props.location?.locationFeatures?.dna}
          onProductPress={this.goToProductDetail}
        />
      );
    }
    return (
      <View style={styles.renderItemView}>
        <MenuItemListTemplate2
          dnaConfiguration={this.props.location.locationFeatures.dna}
          onPress={() => this.goToProductDetail(item)}
          name={item.name}
          item={item}
          price={parseFloat(item.price)}
          imageUrl={imageURL}
          isProductCat={true}
          index={index}
          overrideDna={item.overrideDna}
        />
      </View>
    );
  }

  getTouchableWithoutFeedback = () => {
    return (
      <TouchableWithoutFeedback accessible={false} onPress={Keyboard.dismiss}>
        {this.getKeyboardAvoidingView()}
      </TouchableWithoutFeedback>
    );
  };

  getKeyboardAvoidingView = () => {
    const {selectedCategory} = this.state;
    const key =
      selectedCategory?.type !== CategoryType.Product
        ? selectedCategory?.key
        : '';
    return (
      <>
        <ScrollView contentContainerStyle={styles.contentContainer}>
          <KeyboardAvoidingView behavior="height" insideTab>
            <View style={menuListStyles().content}>
              <View style={menuListStyles().searchContainer}>
                <ProductSearchContainer
                  onChangeText={this.onSearchChanged}
                  searchValue={this.state.searchValue}
                  onPressRecentOrders={this.goToRecentOrders}
                />
              </View>

              <ProductCategories
                categories={this.state.categories}
                selectedCategory={key}
                style={menuListStyles().categories}
                onCategoryPress={this.onCategoryPress}
              />

              {this.state.menuItems?.length &&
              !this.state.searchValue?.trim()?.length ? (
                <QuickAddOns
                  menuItems={this.state.menuItems}
                  selectedCategory={key}
                  onMenuItemPress={this.onMenuItemPress}
                  style={menuListStyles().categories}
                />
              ) : null}

              <FlashList
                data={this.state.items}
                keyExtractor={(item) => item.id}
                renderItem={this.renderItem}
                ListEmptyComponent={this.renderListEmpty}
                estimatedItemSize={72}
                extraData={this.state.refreshKey}
                ListFooterComponent={this.renderFooterComponent}
              />
              {this.renderRewardProductNotAvailableError()}
            </View>
          </KeyboardAvoidingView>
        </ScrollView>
        <CheckoutBar
          location={this.props.location}
          strings={Localized}
          onPress={this.goToCart}
        />
      </>
    );
  };

  render() {
    return (
      <BackSubheader
        title={Localized.Labels.menu}
        testId={'qa_Menu.PageTitle_txt'}
        previousRoute={this.state.previousRoute}
        accessibilityLabel={'Back arrow'}
        accessibilityHint={`Press to navigate back to the ${this.state.previousRoute} screen`}
      >
        {Platform.OS === 'web'
          ? this.getKeyboardAvoidingView()
          : this.getTouchableWithoutFeedback()}
      </BackSubheader>
    );
  }
}

const styles = StyleSheet.create({
  listEmptyStyles: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: Styles.Spacing.m3,
    marginTop:
      Platform.OS === 'web' ? Styles.ScreenHeight / 4 : Styles.ScreenWidth / 3,
  },
  emptyText: {
    fontSize: Styles.Fonts.f7,
    fontWeight: '700',
    color: Styles.Colors.PayNew.black01,
    marginBottom: Styles.Spacing.m2,
  },
  emptyIconContainer: {
    alignSelf: 'center',
  },
  footer: {height: Styles.Heights.h4},
  renderItemView: {flex: 1},
  marginStyle: {
    marginTop: Styles.Spacing.m1,
  },
  contentContainer: {flexGrow: 1},
});

export default withForwardedNavigationParams()(withGlobalize(MenuScreen));
